// Composer - Dynamic input component for Pontifex agent actions
// Theme-aware via CSS variable fallbacks

.composer {
  // Scoped variables with product-agnostic fallbacks
  --composer-accent: var(--accent-primary, rgb(239, 113, 195));
  --composer-accent-glow: var(--accent-glow, rgba(255, 255, 255, 0.12));
  --composer-text: var(--text-primary, rgba(255, 255, 255, 0.85));
  --composer-text-muted: var(--text-secondary, rgba(255, 255, 255, 0.7));
  --composer-surface: var(--surface-overlay, rgba(255, 255, 255, 0.03));
  --composer-border: var(--border-subtle, rgba(255, 255, 255, 0.1));

  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.inputContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
}

// Submit button - shared across all input types
.submitButton {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 2rem;
  border: none;
  border-radius: 22px;
  background: linear-gradient(135deg, var(--composer-accent) 0%, color-mix(in srgb, var(--composer-accent), #000 25%) 100%);
  color: white;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--composer-accent-glow);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Slider input
.sliderContainer {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: 300px;
  padding: 1rem;
}

.sliderEmoji {
  font-size: 2rem;
  flex-shrink: 0;
}

.slider {
  flex: 1;
  height: 8px;
  appearance: none;
  background: var(--composer-border);
  border-radius: 4px;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--composer-accent) 0%, color-mix(in srgb, var(--composer-accent), #000 25%) 100%);
    cursor: pointer;
    transition: transform 0.1s ease;

    &:hover {
      transform: scale(1.15);
    }
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--composer-accent) 0%, color-mix(in srgb, var(--composer-accent), #000 25%) 100%);
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Binary toggle
.toggleContainer {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.toggleOption {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  font-size: 2.5rem;
  background: var(--composer-surface);
  border: 2px solid var(--composer-border);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--composer-surface), transparent 50%);
    border-color: color-mix(in srgb, var(--composer-border), white 30%);
    transform: scale(1.05);
  }

  &.selected {
    background: var(--composer-accent-glow);
    border-color: var(--composer-accent);
    box-shadow: 0 0 20px color-mix(in srgb, var(--composer-accent), transparent 80%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.toggleDivider {
  color: var(--composer-text-muted);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

// Action button row - Shuffle + Continue side by side
.actionButtonRow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

// Action button - circular with gradient border
.actionButton {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(
    135deg,
    var(--gradient-1-stop-1-color) 0%,
    var(--gradient-1-stop-2-color) 25%,
    var(--gradient-1-stop-3-color) 50%,
    var(--gradient-1-stop-4-color) 75%,
    var(--gradient-1-stop-1-color) 100%
  );
  background-size: 300% 300%;
  animation: gradientFlow 8s ease infinite, pulseGlow 3s ease-in-out infinite;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.2),
    0 0 20px var(--composer-accent-glow);

  &:hover:not(:disabled):not(.actionButtonDisabled) {
    transform: scale(1.08);
    box-shadow:
      0 4px 16px rgba(0, 0, 0, 0.3),
      0 0 30px color-mix(in srgb, var(--composer-accent) 30%, transparent);
  }

  &:active:not(:disabled):not(.actionButtonDisabled) {
    transform: scale(0.95);
  }

  &:disabled,
  &.actionButtonDisabled {
    opacity: 0.4;
    cursor: not-allowed;
    animation: gradientFlow 8s ease infinite;
  }

  &:focus-visible {
    outline: 2px solid var(--composer-accent);
    outline-offset: 3px;
  }
}

.actionButtonInner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  border-radius: 50%;
  background: var(--bg);
  color: var(--text);

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.resampleAction:hover:not(:disabled) .actionButtonInner svg {
  animation: spinOnce 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.shuffleAction:hover:not(:disabled) .actionButtonInner svg {
  animation: shuffleSpin 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.continueAction:hover:not(:disabled):not(.actionButtonDisabled) .actionButtonInner svg {
  animation: arrowPulse 0.4s ease;
}

@keyframes gradientFlow {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes pulseGlow {
  0%, 100% {
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.2),
      0 0 20px var(--composer-accent-glow);
  }
  50% {
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.2),
      0 0 30px color-mix(in srgb, var(--composer-accent) 25%, transparent);
  }
}

@keyframes spinOnce {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes shuffleSpin {
  0% { transform: translateX(0); opacity: 1; }
  40% { transform: translateX(8px); opacity: 0; }
  41% { transform: translateX(-8px); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
}

@keyframes arrowPulse {
  0% { transform: translateX(0); }
  50% { transform: translateX(4px); }
  100% { transform: translateX(0); }
}

// Select input
.selectPrompt {
  color: var(--composer-text-muted);
  font-size: 0.9375rem;
  text-align: center;
  margin: 0 0 0.5rem;
}

.selectGrid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  max-width: 400px;
}

.selectOption {
  display: inline-flex;
  align-items: center;
  padding: 0.625rem 1rem;
  background: var(--composer-surface);
  border: 1px solid var(--composer-border);
  border-radius: 20px;
  color: var(--composer-text);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--composer-surface), transparent 50%);
    border-color: color-mix(in srgb, var(--composer-border), white 30%);
  }

  &.selected {
    background: var(--composer-accent-glow);
    border-color: var(--composer-accent);
    color: var(--composer-text);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Writing input — Glass variant
.writingPrompt {
  color: var(--composer-text-muted);
  font-size: 0.9375rem;
  text-align: center;
  margin: 0 0 0.75rem;
}

.writingContainer {
  width: 100%;
  max-width: 400px;
  padding: 1rem 1.25rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus-within {
    border-color: color-mix(in srgb, var(--composer-accent) 30%, transparent);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15), 0 0 20px var(--composer-accent-glow);
  }
}

.writingTextarea {
  width: 100%;
  min-height: 100px;
  background: transparent;
  border: none;
  outline: none;
  color: var(--composer-text);
  font-family: inherit;
  font-size: 0.9375rem;
  line-height: 1.6;
  resize: none;

  &::placeholder {
    color: var(--composer-text-muted);
    opacity: 0.5;
  }

  &:disabled {
    opacity: 0.5;
  }
}
